import clsx from 'clsx';
import * as React from 'react';

import { Icon } from '@gitbook/spine-icons';
import { Button, type ButtonProps, clipboard } from '@gitbook/spine-react';

type CopyButtonProps = Omit<ButtonProps, 'label' | 'leadingIcon' | 'onPress'> & {
    value: string;
    label?: string;
    copiedLabel?: string;
    leadingIcon?: ButtonProps['leadingIcon'];
    onCopy?: () => void;
};

const copiedResetDelay = 1600;

/**
 * Button that copies text and briefly confirms the copy action in place.
 */
export function CopyButton(props: CopyButtonProps) {
    const {
        value,
        label = 'Copy',
        copiedLabel = 'Copied',
        leadingIcon = Icon.Copy,
        onCopy,
        ...buttonProps
    } = props;
    const [isCopied, setIsCopied] = React.useState(false);

    React.useEffect(() => {
        if (!isCopied) {
            return;
        }

        const timeout = window.setTimeout(() => setIsCopied(false), copiedResetDelay);
        return () => window.clearTimeout(timeout);
    }, [isCopied]);

    return (
        <Button
            {...buttonProps}
            label={isCopied ? copiedLabel : label}
            leadingIcon={isCopied ? Icon.Check : leadingIcon}
            tooltip={
                buttonProps.tooltip ?? {
                    label: isCopied ? copiedLabel : label,
                    isOpen: isCopied ? true : undefined,
                }
            }
            onPress={() => {
                void clipboard.writeText(value, { notify: false }).then(
                    () => {
                        setIsCopied(true);
                        onCopy?.();
                    },
                    () => {}
                );
            }}
            className={clsx('transition-all duration-200 ease-snappy-out', buttonProps.className)}
        />
    );
}
